/**
 * useMemberVaultState - Custom hook for Member Vault (Perks) module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialPerkForm = {
  partnerLogo: '',
  partnerName: '',
  title: '',
  description: '',
  code: '',
  expirationDate: '',
  details: '',
  termsUrl: '',
  ctaLabel: 'SHOP NOW',
  ctaUrl: '',
  category: '',
  isActive: true,
  isFeatured: false,
  isNew: true
};

const initialSponsorLogoForm = {
  logoUrl: '',
  name: '',
  linkUrl: '',
  isActive: true,
  isNew: true
};

export const useMemberVaultState = ({ setError, setSuccess }) => {
  // Perks state
  const [perks, setPerks] = useState([]);
  const [loadingPerks, setLoadingPerks] = useState(false);
  const [editingPerk, setEditingPerk] = useState(null);
  const [showPerkModal, setShowPerkModal] = useState(false);

  // Category filter state
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [perkCategories, setPerkCategories] = useState([]);

  // Sponsor Logos state
  const [sponsorLogos, setSponsorLogos] = useState([]);
  const [loadingSponsorLogos, setLoadingSponsorLogos] = useState(false);
  const [editingSponsorLogo, setEditingSponsorLogo] = useState(null);
  const [showSponsorLogoModal, setShowSponsorLogoModal] = useState(false);

  // Tab state
  const [lockerTab, setLockerTab] = useState('perks'); // 'perks' | 'sponsors' | 'analytics'

  // ============================================
  // PERKS FUNCTIONS
  // ============================================

  const fetchPerkCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clubhouse/admin/perks/categories`);
      setPerkCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchPerks = useCallback(async () => {
    setLoadingPerks(true);
    try {
      const response = await axios.get(`${API_URL}/api/clubhouse/admin/perks`, {
        headers: getAuthHeaders()
      });
      setPerks(response.data);
    } catch (err) {
      console.error('Error fetching perks:', err);
      setError('Failed to load perks');
    } finally {
      setLoadingPerks(false);
    }
  }, [setError]);

  // Filter perks by category
  const filteredPerks = categoryFilter === 'all' 
    ? perks 
    : perks.filter(p => (p.category || 'Other') === categoryFilter);

  const handleCreatePerk = useCallback(() => {
    setEditingPerk({ ...initialPerkForm });
    setShowPerkModal(true);
  }, []);

  const handleEditPerk = useCallback((perk) => {
    setEditingPerk({ ...perk });
    setShowPerkModal(true);
  }, []);

  const handleSavePerk = useCallback(async () => {
    if (!editingPerk) return;

    if (!editingPerk.title?.trim()) {
      setError('Perk title is required');
      return;
    }

    try {
      if (editingPerk.isNew) {
        const { isNew, ...perkData } = editingPerk;
        await axios.post(`${API_URL}/api/clubhouse/admin/perks`, perkData, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingPerk.title}" created successfully!`);
      } else {
        await axios.put(`${API_URL}/api/clubhouse/admin/perks/${editingPerk.id}`, editingPerk, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingPerk.title}" saved successfully!`);
      }

      setShowPerkModal(false);
      setEditingPerk(null);
      fetchPerks();
    } catch (err) {
      setError('Failed to save perk: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingPerk, fetchPerks, setError, setSuccess]);

  const handleDeletePerk = useCallback(async (perkId) => {
    const perk = perks.find(p => p.id === perkId);
    if (!window.confirm(`Are you sure you want to delete "${perk?.title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/clubhouse/admin/perks/${perkId}`, {
        headers: getAuthHeaders()
      });
      setPerks(prev => prev.filter(p => p.id !== perkId));
      setSuccess('Perk deleted successfully');
    } catch (err) {
      setError('Failed to delete perk: ' + (err.response?.data?.detail || err.message));
    }
  }, [perks, setError, setSuccess]);

  const handleTogglePerkActive = useCallback(async (perk) => {
    try {
      await axios.put(`${API_URL}/api/clubhouse/admin/perks/${perk.id}`, {
        ...perk,
        isActive: !perk.isActive
      }, {
        headers: getAuthHeaders()
      });
      setPerks(prev => prev.map(p => p.id === perk.id ? { ...p, isActive: !p.isActive } : p));
    } catch (err) {
      setError('Failed to update perk status');
    }
  }, [setError]);

  const handleTogglePerkFeatured = useCallback(async (perk) => {
    try {
      await axios.put(`${API_URL}/api/clubhouse/admin/perks/${perk.id}`, {
        ...perk,
        isFeatured: !perk.isFeatured
      }, {
        headers: getAuthHeaders()
      });
      setPerks(prev => prev.map(p => p.id === perk.id ? { ...p, isFeatured: !p.isFeatured } : p));
    } catch (err) {
      setError('Failed to update perk featured status');
    }
  }, [setError]);

  // ============================================
  // SPONSOR LOGOS FUNCTIONS
  // ============================================

  const fetchSponsorLogos = useCallback(async () => {
    setLoadingSponsorLogos(true);
    try {
      const response = await axios.get(`${API_URL}/api/clubhouse/admin/sponsor-logos`, {
        headers: getAuthHeaders()
      });
      setSponsorLogos(response.data);
    } catch (err) {
      console.error('Error fetching sponsor logos:', err);
    } finally {
      setLoadingSponsorLogos(false);
    }
  }, []);

  const handleCreateSponsorLogo = useCallback(() => {
    setEditingSponsorLogo({ ...initialSponsorLogoForm });
    setShowSponsorLogoModal(true);
  }, []);

  const handleEditSponsorLogo = useCallback((logo) => {
    setEditingSponsorLogo({ ...logo });
    setShowSponsorLogoModal(true);
  }, []);

  const handleSaveSponsorLogo = useCallback(async () => {
    if (!editingSponsorLogo) return;

    if (!editingSponsorLogo.name?.trim()) {
      setError('Sponsor name is required');
      return;
    }

    try {
      if (editingSponsorLogo.isNew) {
        const { isNew, ...logoData } = editingSponsorLogo;
        await axios.post(`${API_URL}/api/clubhouse/admin/sponsor-logos`, logoData, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingSponsorLogo.name}" created successfully!`);
      } else {
        await axios.put(`${API_URL}/api/clubhouse/admin/sponsor-logos/${editingSponsorLogo.id}`, editingSponsorLogo, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingSponsorLogo.name}" saved successfully!`);
      }

      setShowSponsorLogoModal(false);
      setEditingSponsorLogo(null);
      fetchSponsorLogos();
    } catch (err) {
      setError('Failed to save sponsor logo: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingSponsorLogo, fetchSponsorLogos, setError, setSuccess]);

  const handleDeleteSponsorLogo = useCallback(async (logoId) => {
    const logo = sponsorLogos.find(l => l.id === logoId);
    if (!window.confirm(`Are you sure you want to delete "${logo?.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/clubhouse/admin/sponsor-logos/${logoId}`, {
        headers: getAuthHeaders()
      });
      setSponsorLogos(prev => prev.filter(l => l.id !== logoId));
      setSuccess('Sponsor logo deleted successfully');
    } catch (err) {
      setError('Failed to delete sponsor logo: ' + (err.response?.data?.detail || err.message));
    }
  }, [sponsorLogos, setError, setSuccess]);

  // Notify members about a new perk
  const handleNotifyMembers = useCallback(async (perkId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/clubhouse/admin/perks/${perkId}/notify-members`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message || 'Members notified successfully!');
      fetchPerks(); // Refresh to show notification status
    } catch (err) {
      setError('Failed to send notifications: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchPerks, setError, setSuccess]);

  return {
    // Perks state
    perks,
    filteredPerks,
    loadingPerks,
    editingPerk,
    showPerkModal,
    categoryFilter,
    perkCategories,

    // Sponsor Logos state
    sponsorLogos,
    loadingSponsorLogos,
    editingSponsorLogo,
    showSponsorLogoModal,

    // Tab state
    lockerTab,

    // Setters
    setPerks,
    setEditingPerk,
    setShowPerkModal,
    setSponsorLogos,
    setEditingSponsorLogo,
    setShowSponsorLogoModal,
    setLockerTab,
    setCategoryFilter,

    // Perks actions
    fetchPerks,
    fetchPerkCategories,
    handleCreatePerk,
    handleEditPerk,
    handleSavePerk,
    handleDeletePerk,
    handleTogglePerkActive,
    handleTogglePerkFeatured,
    handleNotifyMembers,

    // Sponsor Logos actions
    fetchSponsorLogos,
    handleCreateSponsorLogo,
    handleEditSponsorLogo,
    handleSaveSponsorLogo,
    handleDeleteSponsorLogo,
  };
};

export default useMemberVaultState;
